import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import LanguageSelector from '@/components/LanguageSelector';
import logoRideau from '@/assets/logo-rideau.png';
import { prefetchPage } from "@/App";

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();

  const navigation = [
    { name: t('nav.home'), href: '/' },
    { name: t('nav.marketAccess'), href: '/partenaires' },
    { name: t('nav.company'), href: '/societe' },
    { name: t('nav.products'), href: '/produits' },
    { name: t('nav.contact'), href: '/contact' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-background/95 backdrop-blur-md shadow-elegant py-3'
          : 'bg-transparent py-6'
      }`}
    >
      <div className="container-premium">
        <nav className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" onClick={() => window.scrollTo({ top: 0 })} className="flex items-center gap-2 sm:gap-3 group">
            <img
              src={logoRideau}
              alt="SAS RIDEAU"
              className="h-10 w-10 sm:h-12 sm:w-12 rounded-full object-cover transition-transform duration-300 group-hover:scale-105"
            />
            <span className="font-serif text-base sm:text-xl font-semibold tracking-wide text-foreground">
              SAS RIDEAU
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navigation.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => window.scrollTo({ top: 0 })}
                onMouseEnter={() => prefetchPage(item.href)}
                onTouchStart={() => prefetchPage(item.href)}
                className={`link-underline text-sm font-medium tracking-wide transition-colors duration-300 ${
                  location.pathname === item.href
                    ? 'text-accent'
                    : 'text-foreground/80 hover:text-accent'
                }`}
              >
                {item.name}
              </Link>
            ))}
          </div>

          {/* CTA Button + Language Selector */}
          <div className="hidden lg:flex items-center gap-4">
            <LanguageSelector />
            <Link
              to="/contact"
              onClick={() => window.scrollTo({ top: 0 })}
              onMouseEnter={() => prefetchPage('/contact')}
              onTouchStart={() => prefetchPage('/contact')}
            >
              <Button variant="gold" size="default">
                {t('nav.contactUs')}
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center gap-3">
            <LanguageSelector />
            <button
              className="p-2 text-foreground"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Menu"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </nav>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden border-t border-accent/20"
            style={{
              background: 'linear-gradient(180deg, rgba(26, 39, 68, 0.97) 0%, rgba(20, 32, 56, 0.98) 100%)',
              backdropFilter: 'blur(16px)',
            }}
          >
            <div className="container-premium py-10 flex flex-col items-center text-center">
              {navigation.map((item, index) => (
                <motion.div
                  key={item.href}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                >
                  <Link
                    to={item.href}
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      window.scrollTo({ top: 0 });
                    }}
                    onMouseEnter={() => prefetchPage(item.href)}
                    onTouchStart={() => prefetchPage(item.href)}
                    className={`block text-xl font-semibold py-3 tracking-wider transition-all duration-300 active:scale-95 ${
                      location.pathname === item.href
                        ? 'text-accent'
                        : 'text-foreground/90 hover:text-accent'
                    }`}
                  >
                    {item.name}
                  </Link>
                  {index < navigation.length - 1 && (
                    <div className="w-12 h-px bg-accent/20 mx-auto" />
                  )}
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: navigation.length * 0.05 }}
                className="mt-8 w-full"
              >
                <Link
                  to="/contact"
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    window.scrollTo({ top: 0 });
                  }}
                  onMouseEnter={() => prefetchPage('/contact')}
                  onTouchStart={() => prefetchPage('/contact')}
                >
                  <Button variant="gold" className="w-full font-semibold py-5">
                    {t('nav.contactUs')}
                  </Button>
                </Link>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
